/* eslint-disable max-len */
import {
  Address, AnalyticsCartDto, ArtistPageInfo, BrandPageInfo, BusinessLevelMarketingInfo, BusinessLevelMarketingSubscriber,
  BusinessModuleRef, CustomerAccount, CustomerOrderBase, CustomerProfile,
  CustomerSubscription, DataModel, DiscountCodeBase, GiftCardDetails, Voucher, Language, LocalizedRichDescription, MapUtils, NfcPassPageInfo, OnlineCartDto,
  OnlineEventTicketOrder, OpenAccessPassDetails, OperationResponse, OrganizerPageInfo, PoiReview,
  PoliciesSettings, PoliciesSettingsEventOverride, PoliciesSettingsWithEventOverrides, SerializedData,
  TaxIdentificationDetails, TicketSupplementTemplate, TicketTemplate, VoidOperationResponse,
  createSerializationContext, fromData, fromEnumAsString, toData, toDataOrNull,
  tryFromNonNullableData
} from 'in-time-core';
import { Injectable, inject } from '@angular/core';
import {
  CloudFunctionsException, GCPCloudFunctionsProvider, ICloudFunctionsProvider
} from 'cloud-functions';
import {
  IApiEndpointProvider, ApiEndpointProvider,
  EmulatorApiEndpointProvider
} from './utility/api-endpoint-provider';
import {
  CloudFunctionsHttpClient,
} from './utility/cloud-functions-http-client';
import { AuthenticationService } from './authentication.service';
import { ErrorType, parseInTimeApiError } from '../core/models/error-type';
import { logError, logWarning } from 'in-time-logger';
import { environment } from '../app.environment';
import { createHttpsAuthContext } from '../core/utils/cloud-function-utils';
import { filter, first, firstValueFrom } from 'rxjs';
import { ContentViewEventArgs, PageViewEventArgs, SearchEventArgs } from './analytics/analytics.types';

interface ClientData {
  client: CustomerProfile,
  subscription: CustomerSubscription,
}

export type InvoiceKind = 'ORDER' | 'SUPPLEMENT' | 'FEE' | 'DONATION' | 'PRODUCTS';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly cloudFunctionsProvider: ICloudFunctionsProvider;
  private readonly endpointProvider: IApiEndpointProvider;

  private readonly authenticationService = inject(AuthenticationService);

  constructor() {
    const cloudFunctionsProvider = new GCPCloudFunctionsProvider({
      httpClient: new CloudFunctionsHttpClient(),
      gcpServiceUrl: `${environment.apiGatewayUrl}/api`,
      appId: 'in-time-web',
      buildId: null,
      operatingSystem: 'browser',
    });

    if(environment.useApiEmulator) {
      cloudFunctionsProvider.useFunctionsEmulator('http://localhost:8081');
    }

    this.cloudFunctionsProvider = cloudFunctionsProvider;
    this.endpointProvider = environment.useApiEmulator ?
      EmulatorApiEndpointProvider:
      ApiEndpointProvider;
  }

  async unlockPromoTicket(params: {
    businessRef: BusinessModuleRef,
    eventId: string,
    ticketId: string,
    promoCode: string,
    customerName: string,
    customerEmail: string,
    customerLanguage: Language,
    customerAddress: Address | null,
  }): Promise<OperationResponse<CustomerOrderBase>> {
    const endpoint = this.endpointProvider.unlockPromoTicket;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'businessRef': params.businessRef.encode(),
          'eventId': params.eventId,
          'ticketId': params.ticketId,
          'promoCode': params.promoCode,
          'customerName': params.customerName,
          'customerEmail': params.customerEmail,
          'customerLanguage': fromEnumAsString(params.customerLanguage),
          'customerAddress': toDataOrNull(params.customerAddress),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const orderResult = tryFromNonNullableData<CustomerOrderBase>(response.data['result'] as SerializedData);
        if(orderResult.isError) {
          return OperationResponse.error(ErrorType.Unknown);
        }

        return OperationResponse.success(orderResult.value);
      }
      else {
        const rawErrorCode = MapUtils.getOrNull<string>(response.data, 'error');
        logError(`Failed to unlock promo ticket because of a cloud error: ${rawErrorCode}`);

        const errorType = parseInTimeApiError(rawErrorCode ?? ErrorType.ApiInternalError);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async createCustomerAccount(
    account: CustomerAccount,
    options: { allowPlatformMarketing: boolean | null }
  ): Promise<OperationResponse<CustomerAccount>> {
    const endpoint = this.endpointProvider.createCustomerAccount;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'account': toData(account),
          'allowPlatformMarketing': options.allowPlatformMarketing,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const accountResult = tryFromNonNullableData<CustomerAccount>(response.data['result'] as SerializedData);
        if(accountResult.isError) {
          return OperationResponse.error(ErrorType.Unknown);
        }

        return OperationResponse.success(accountResult.value);
      }
      else {
        const rawErrorCode = MapUtils.getOrNull<string>(response.data, 'error');
        logError(`Failed to create customer account because of a cloud error: ${rawErrorCode}`);

        const errorType = parseInTimeApiError(rawErrorCode ?? ErrorType.ApiInternalError);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchCheckoutDiscountCodes(args: {
    eventId: string,
    code: string,
  }): Promise<OperationResponse<DiscountCodeBase[]>> {
    const endpoint = this.endpointProvider.fetchCheckoutDiscountCodes;
    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(endpoint, context);
      const response = await callable.call({ parameters: {
        eventId: args.eventId,
        code: args.code.trim().toUpperCase(),
      } });
      if(MapUtils.getBooleanOrNull(response.data, 'success') !== true) {
        return OperationResponse.error(parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.ApiInternalError));
      }

      const candidates = MapUtils.getList<DataModel>(response.data, 'result', (value) => fromData<DataModel>(value as SerializedData));
      const discountCodes = candidates.filter((candidate): candidate is Voucher | OpenAccessPassDetails | GiftCardDetails =>
        Voucher.assertType(candidate) || OpenAccessPassDetails.assertType(candidate) || GiftCardDetails.assertType(candidate));
      return OperationResponse.success(discountCodes);
    }
    catch(error) {
      logError('Failed to fetch checkout discount codes: ', error);
      return OperationResponse.error(ErrorType.ApiInternalError);
    }
  }

  async confirmPageView(args: PageViewEventArgs): Promise<void> {
    const endpoint = this.endpointProvider.confirmPageView;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          deduplicationId: args.deduplicationId,
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async confirmViewContent(args: ContentViewEventArgs): Promise<void> {
    const endpoint = this.endpointProvider.confirmViewContent;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          contentCategory: args.category,
          contentName: args.title,
          eventId: args.contentId,
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async confirmSearch(args: SearchEventArgs): Promise<void> {
    const endpoint = this.endpointProvider.confirmSearch;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          searchString: args.query,
          contentCategory: args.category
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async confirmAddToCart(analyticsCartDto: AnalyticsCartDto): Promise<void> {
    const endpoint = this.endpointProvider.confirmAddToCart;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          cart: toData(analyticsCartDto)
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async confirmInitiateCheckout(analyticsCartDto: AnalyticsCartDto): Promise<void> {
    const endpoint = this.endpointProvider.confirmInitiateCheckout;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          cart: toData(analyticsCartDto)
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async confirmCompleteRegistration(analyticsCartDto: AnalyticsCartDto): Promise<void> {
    const endpoint = this.endpointProvider.confirmCompleteRegistration;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          cart: toData(analyticsCartDto)
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async confirmInitiatePurchase(analyticsCartDto: AnalyticsCartDto): Promise<void> {
    const endpoint = this.endpointProvider.confirmInitiatePurchase;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          cart: toData(analyticsCartDto)
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async confirmPurchaseComplete(email: string | null, analyticsCartDto: AnalyticsCartDto): Promise<void> {
    const endpoint = this.endpointProvider.confirmPurchaseComplete;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      await callable.call({
        parameters: {
          url: window.location.href,
          cart: toData(analyticsCartDto),
          email: email,
        }
      });
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
    }
  }

  async createOnlineOrder(cart: OnlineCartDto, options: { allowPoliciesMarketing?: boolean } = {}): Promise<OperationResponse<CustomerOrderBase>> {
    const endpoint = this.endpointProvider.createOnlineOrder;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'cart': toData(cart),
          ...(options.allowPoliciesMarketing === true ? { allowPoliciesMarketing: true } : {}),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const orderResult = tryFromNonNullableData<CustomerOrderBase>(response.data['result'] as SerializedData);
        if(orderResult.isError) {
          logError(`Failed to deserialize online order from API response: ${orderResult.error}`);
          return OperationResponse.error(ErrorType.Unknown);
        }

        return OperationResponse.success(orderResult.value);
      }
      else {
        const rawErrorCode = MapUtils.getOrNull<string>(response.data, 'error');
        logError(`Failed to create online order because of a cloud error: ${rawErrorCode}`);

        const errorType = parseInTimeApiError(rawErrorCode ?? ErrorType.ApiInternalError);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async sanitizeGenericVoucherCart(cart: OnlineCartDto): Promise<OperationResponse<OnlineCartDto>> {
    const endpoint = this.endpointProvider.sanitizeGenericVoucherCart;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: { 'cart': toData(cart) }
      });

      const success = MapUtils.getBooleanOrNull(response.data, 'success') ?? false;
      if(success) {
        const orderResult = tryFromNonNullableData<OnlineCartDto>(response.data['result'] as SerializedData);
        if(orderResult.isError) {
          logError(`Failed to deserialize online cart from API response: ${orderResult.error}`);
          return OperationResponse.error(ErrorType.Unknown);
        }

        return OperationResponse.success(orderResult.value);
      }
      else {
        const rawErrorCode = MapUtils.getStringOrNull(response.data, 'error');
        logError(`Failed to sanitize online cart because of a cloud error: ${rawErrorCode}`);

        const errorType = parseInTimeApiError(rawErrorCode ?? ErrorType.ApiInternalError);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async markOrderWithFailedPayment(
    order: CustomerOrderBase,
  ): Promise<OperationResponse<CustomerOrderBase>> {
    const endpoint = this.endpointProvider.markOrderWithFailedPayment;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'orderId': order.uniqueId,
          'customerId': order.userId ?? null,
          'isGuestOrder': order.isGuestOrder,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const orderResult = tryFromNonNullableData<CustomerOrderBase>(response.data['result'] as SerializedData);
        if(orderResult.isError) {
          logError(`Failed to deserialize order {${order.uniqueId}} of customer {${order.creatorId}} from API response: ${orderResult.error}`);
          return OperationResponse.error(ErrorType.Unknown);
        }

        return OperationResponse.success(orderResult.value);
      }
      else {
        const rawErrorCode = MapUtils.getOrNull<string>(response.data, 'error');
        logError(`Failed to mark order {${order.uniqueId}} of customer {${order.creatorId}} with failed payment because of a cloud error: ${rawErrorCode}`);

        const errorType = parseInTimeApiError(rawErrorCode ?? ErrorType.ApiInternalError);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async abortOrder(
    order: CustomerOrderBase,
  ): Promise<OperationResponse<CustomerOrderBase>> {
    const endpoint = this.endpointProvider.abortOrder;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'orderId': order.uniqueId,
          'customerId': order.userId ?? null,
          'isGuestOrder': order.isGuestOrder,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const orderResult = tryFromNonNullableData<CustomerOrderBase>(response.data['result'] as SerializedData);
        if(orderResult.isError) {
          logError(`Failed to deserialize order {${order.uniqueId}} of customer {${order.creatorId}} from API response: ${orderResult.error}`);
          return OperationResponse.error(ErrorType.Unknown);
        }

        return OperationResponse.success(orderResult.value);
      }
      else {
        const rawErrorCode = MapUtils.getOrNull<string>(response.data, 'error');
        logError(`Failed to abort order {${order.uniqueId}} of customer {${order.creatorId}} because of a cloud error: ${rawErrorCode}`);

        const errorType = parseInTimeApiError(rawErrorCode ?? ErrorType.ApiInternalError);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async lockSeatSelection(selection: {
    businessRef: BusinessModuleRef,
    eventId: string,
    sessionId: string,
    cartItems: { ticket: TicketTemplate, seats: number[] }[]
  }): Promise<VoidOperationResponse> {
    const endpoint = this.endpointProvider.lockClientSeatSelection;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'businessRef': selection.businessRef.encode(),
          'eventId': selection.eventId,
          'sessionId': selection.sessionId,
          'cartItems': selection.cartItems.map((c) => ({
            'ticket': toData(c.ticket),
            'seats': c.seats,
          })),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        return VoidOperationResponse.success();
      }
      else {
        const rawErrorCode = MapUtils.getOrNull<string>(response.data, 'error');
        logError(`Failed to lock seat selection for event {${selection.eventId}} of business {${selection.businessRef.uniqueId}} because of a cloud error: ${rawErrorCode}`);

        const errorType = parseInTimeApiError(rawErrorCode ?? ErrorType.ApiInternalError);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return VoidOperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async createPoiReview(review: PoiReview): Promise<VoidOperationResponse> {
    const endpoint = this.endpointProvider.createPoiReview;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'review': toData(review),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        return VoidOperationResponse.success();
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to create poi review {${review.uniqueId}} of business {${review.businessRef.uniqueId}} because of a cloud error: ${errorType}`);
        return VoidOperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return VoidOperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async createClientFromCustomerAccount(
    account: CustomerAccount,
    businessRef: BusinessModuleRef,
    owningStaffId: string | null = null,
  ): Promise<OperationResponse<ClientData>> {
    const endpoint = this.endpointProvider.createClientFromCustomerAccount;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'businessRef': businessRef.encode(),
          'owningStaffId': owningStaffId,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const map = response.data['result'] as SerializedData;
        const clientResult = tryFromNonNullableData<CustomerProfile>(
          MapUtils.get<SerializedData>(map, 'client'),
        );

        const subscriptionResult = tryFromNonNullableData<CustomerSubscription>(
          MapUtils.get<SerializedData>(map, 'subscription'),
        );

        if(clientResult.isError) {
          // eslint-disable-next-line max-len
          logError(`Failed to deserialize client profile from API response for account {${account.uniqueId}} to business {${businessRef}}: ${clientResult.error}`);
          return OperationResponse.error(ErrorType.Unknown);
        }

        if(subscriptionResult.isError) {
          // eslint-disable-next-line max-len
          logError(`Failed to deserialize client subscription from API response for account {${account.uniqueId}} to business {${businessRef}}: ${subscriptionResult.error}`);
          return OperationResponse.error(ErrorType.Unknown);
        }

        return OperationResponse.success({
          client: clientResult.value,
          subscription: subscriptionResult.value,
        });
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to create client from customer account to business {${businessRef.encode()}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async validateTaxForm(requestId: string, details: TaxIdentificationDetails): Promise<VoidOperationResponse> {
    const endpoint = this.endpointProvider.validateTaxForm;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'requestId': requestId,
          'details': toData(details),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        return VoidOperationResponse.success();
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to validate tax form {${requestId}} because of a cloud error: ${errorType}`);
        return VoidOperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return VoidOperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchArtistPageInfo(
    webRoute: string,
  ): Promise<OperationResponse<ArtistPageInfo>> {
    const endpoint = this.endpointProvider.fetchArtistPageInfo;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'webRoute': webRoute,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData;
        const artistPageInfo = fromData<ArtistPageInfo>(data);

        return OperationResponse.success(artistPageInfo);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to fetch artist page info for web route {${webRoute}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchBrandPageInfo(
    webRoute: string
  ): Promise<OperationResponse<BrandPageInfo>> {
    const endpoint = this.endpointProvider.fetchBrandPageInfo;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'webRoute': webRoute,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData;
        const brandPageInfo = fromData<BrandPageInfo>(data);

        return OperationResponse.success(brandPageInfo);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to fetch brand page info for web route {${webRoute}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchOrganizerPageInfo(
    webRoute: string,
  ): Promise<OperationResponse<OrganizerPageInfo>> {
    const endpoint = this.endpointProvider.fetchOrganizerPageInfo;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'webRoute': webRoute,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData;
        const organizerPageInfo = fromData<OrganizerPageInfo>(data);

        return OperationResponse.success(organizerPageInfo);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to fetch organizer page info for web route {${webRoute}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchTicketSupplementTemplates(
    businessRef: BusinessModuleRef,
  ): Promise<OperationResponse<TicketSupplementTemplate[]>> {
    const endpoint = this.endpointProvider.fetchTicketSupplementTemplates;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'businessRef': businessRef.encode(),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData[];
        const supplementTemplates = data.map((e) => fromData<TicketSupplementTemplate>(e));

        return OperationResponse.success(supplementTemplates);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to fetch ticket supplement templates for business {${businessRef}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchProductDescription(
    businessRef: BusinessModuleRef,
  ): Promise<OperationResponse<LocalizedRichDescription | null>> {
    const endpoint = this.endpointProvider.fetchProductDescription;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'businessRef': businessRef.encode(),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData | null;
        const productsDescription = data == null ? null : fromData<LocalizedRichDescription>(data);

        return OperationResponse.success(productsDescription);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to fetch product description for business {${businessRef}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchPoliciesSettings(businessRef: BusinessModuleRef): Promise<OperationResponse<PoliciesSettingsWithEventOverrides>> {
    const endpoint = this.endpointProvider.fetchPoliciesSettings;

    try {
      // Initial route resolution can run before the app restores its sign-in state.
      const user = await this.authenticationService.getCurrentFirebaseUser();
      const context = await createHttpsAuthContext(user);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(endpoint, context);
      const response = await callable.call({
        parameters: { businessRef: businessRef.encode() },
      });

      if(MapUtils.getBooleanOrNull(response.data, 'success') ?? false) {
        const result = MapUtils.getRecord(response.data, 'result');
        const settings = fromData<PoliciesSettings>(MapUtils.getRecord(result, 'settings'));
        const eventOverrides = MapUtils.getList<PoliciesSettingsEventOverride>(result, 'eventOverrides', (value) => {
          const data = value as SerializedData;
          return {
            eventId: MapUtils.getString(data, 'eventId'),
            override: fromData<PoliciesSettings>(MapUtils.getRecord(data, 'override')),
          };
        });
        if(!(settings instanceof PoliciesSettings) || eventOverrides.some((item) => !(item.override instanceof PoliciesSettings))) {
          logError(`Failed to deserialize policies settings for business {${businessRef}}.`);
          return OperationResponse.error(ErrorType.DatabaseError);
        }

        return OperationResponse.success({ settings, eventOverrides });
      }

      const errorType = parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.DatabaseError);
      logWarning(`Failed to fetch policies settings for business {${businessRef}}: ${errorType}`);
      return OperationResponse.error(errorType);
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchRegistrationSettings(
    businessRef: BusinessModuleRef,
  ): Promise<OperationResponse<{ isPhoneNumberMandatoryForGuests: boolean }>> {
    const endpoint = this.endpointProvider.fetchRegistrationSettings;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'businessRef': businessRef.encode(),
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData;
        const isPhoneNumberMandatoryForGuests = MapUtils.getBooleanOrNull(data, 'isPhoneNumberMandatoryForGuests') ?? false;

        return OperationResponse.success({ isPhoneNumberMandatoryForGuests });
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to fetch registration settings for business {${businessRef}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchNfcPassPageInfo(
    passId: string,
  ): Promise<OperationResponse<NfcPassPageInfo | null>> {
    const endpoint = this.endpointProvider.fetchNfcPassPageInfo;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'passId': passId,
        }
      });

      const success = MapUtils.getOrNull<boolean>(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData;
        const pass = NfcPassPageInfo.fromMapOrNull(createSerializationContext(), data);

        return OperationResponse.success(pass);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getOrNull<string>(response.data, 'error') ?? ErrorType.DatabaseError);
        logError(`Failed to generate NFC pass page data for pass {${passId}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  // async fetchBusinessLevelMarketingStatus(
  //   businessRef: BusinessModuleRef
  // ): Promise<OperationResponse<BusinessLevelMarketingInfo>> {
  //   const endpoint = this.endpointProvider.fetchBusinessLevelMarketingInfo;

  //   try {
  //     const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
  //     const callable = this.cloudFunctionsProvider.getHttpsCallable(
  //       endpoint,
  //       context,
  //     );

  //     const response = await callable.call({
  //       parameters: {
  //         'businessRef': businessRef.encode(),
  //       }
  //     });

  //     const success = MapUtils.getBooleanOrNull(response.data, 'success') ?? false;
  //     if(success) {
  //       const result = MapUtils.getRecord(response.data, 'result');
  //       const info = fromData<BusinessLevelMarketingInfo>(result);

  //       return OperationResponse.success(info);
  //     }
  //     else {
  //       const errorType = parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.DatabaseError);
  //       logError(`Failed to check if subscribing is enabled for business {${businessRef}} because of a cloud error: ${errorType}`);
  //       return OperationResponse.error(errorType);
  //     }
  //   }
  //   catch(error) {
  //     logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
  //     return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
  //   }
  // }

  async fetchBusinessLevelMarketingInfo(
    businessRef: BusinessModuleRef
  ): Promise<OperationResponse<BusinessLevelMarketingInfo>> {
    const endpoint = this.endpointProvider.fetchBusinessLevelMarketingInfo;

    try {
      const context = await createHttpsAuthContext(this.authenticationService.authState?.user ?? null);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'businessRef': businessRef.encode(),
        }
      });

      const success = MapUtils.getBooleanOrNull(response.data, 'success') ?? false;
      if(success) {
        const data = response.data['result'] as SerializedData;
        const info = fromData<BusinessLevelMarketingInfo>(data);
        if(!(info instanceof BusinessLevelMarketingInfo)) {
          logError(`Failed to fetch marketing info for business {${businessRef}} because, got data of type {${(info as object).constructor.name}} and expected {BusinessLevelMarketingInfo}.`);
          return OperationResponse.error(ErrorType.DatabaseError);
        }

        return OperationResponse.success(info);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.DatabaseError);
        logWarning(`Failed to fetch marketing info for business {${businessRef}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async fetchBusinessLevelMarketingSubscriber(businessRef: BusinessModuleRef): Promise<OperationResponse<BusinessLevelMarketingSubscriber>> {
    const endpoint = this.endpointProvider.fetchBusinessLevelMarketingSubscriber;

    try {
      const authState = this.authenticationService.authState;
      if(!authState.isAuthenticated || !authState.user || authState.isAnonymous) {
        return OperationResponse.error(ErrorType.NotAuthenticated);
      }

      const context = await createHttpsAuthContext(authState.user);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(endpoint, context);
      const response = await callable.call({ parameters: { businessRef: businessRef.encode() } });
      if(MapUtils.getBooleanOrNull(response.data, 'success') ?? false) {
        const data = MapUtils.getRecordOrNull(response.data, 'result');
        if(data == null) return OperationResponse.success<BusinessLevelMarketingSubscriber>();

        const subscriber = fromData<BusinessLevelMarketingSubscriber>(data);
        if(!(subscriber instanceof BusinessLevelMarketingSubscriber)) {
          return OperationResponse.error(ErrorType.DatabaseError);
        }
        return OperationResponse.success(subscriber);
      }

      return OperationResponse.error(parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.DatabaseError));
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async validateOrderForDownload({ orderId, customerId, isGuestOrder }: {
    orderId: string,
    customerId: string,
    isGuestOrder: boolean,
  }): Promise<OperationResponse<OnlineEventTicketOrder>> {
    const endpoint = this.endpointProvider.validateOrderForDownload;

    try {
      if(this.authenticationService.isSigningIn) {
        await firstValueFrom(this.authenticationService.isSigningIn$.pipe(
          filter((x) => x === false),
          first()
        ));
      }

      const context = await createHttpsAuthContext(this.authenticationService.authState?.user);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'orderId': orderId,
          'customerId': customerId,
          'isGuestOrder': isGuestOrder,
        }
      });

      const success = MapUtils.getBooleanOrNull(response.data, 'success') ?? false;
      if(success) {
        const data = MapUtils.getRecord(response.data, 'result');
        const result = fromData<OnlineEventTicketOrder>(data);

        return OperationResponse.success(result);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.Unknown);
        logWarning(`Failed to validate online ticket order for download {${orderId}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async downloadTicket({ orderId, customerId, isGuestOrder, ticketIndex }: {
    orderId: string,
    customerId: string,
    isGuestOrder: boolean,
    ticketIndex: number,
  }): Promise<OperationResponse<string>> {
    const endpoint = this.endpointProvider.downloadTicket;

    try {
      if(this.authenticationService.isSigningIn) {
        await firstValueFrom(this.authenticationService.isSigningIn$.pipe(
          filter((x) => x === false),
          first()
        ));
      }

      const context = await createHttpsAuthContext(this.authenticationService.authState?.user);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'orderId': orderId,
          'customerId': customerId,
          'isGuestOrder': isGuestOrder,
          'ticketIndex': ticketIndex,
        }
      });

      const success = MapUtils.getBooleanOrNull(response.data, 'success') ?? false;
      if(success) {
        const data = MapUtils.getString(response.data, 'result');
        return OperationResponse.success(data);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.Unknown);
        logWarning(`Failed to download ticket {${ticketIndex}} for order {${orderId}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async downloadTicketSupplement({ orderId, customerId, isGuestOrder, ticketIndex }: {
    orderId: string,
    customerId: string,
    isGuestOrder: boolean,
    ticketIndex: number,
  }): Promise<OperationResponse<string>> {
    const endpoint = this.endpointProvider.downloadTicketSupplement;

    try {
      if(this.authenticationService.isSigningIn) {
        await firstValueFrom(this.authenticationService.isSigningIn$.pipe(
          filter((x) => x === false),
          first()
        ));
      }

      const context = await createHttpsAuthContext(this.authenticationService.authState?.user);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'orderId': orderId,
          'customerId': customerId,
          'isGuestOrder': isGuestOrder,
          'ticketIndex': ticketIndex,
        }
      });

      const success = MapUtils.getBooleanOrNull(response.data, 'success') ?? false;
      if(success) {
        const data = MapUtils.getString(response.data, 'result');
        return OperationResponse.success(data);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.Unknown);
        logWarning(`Failed to download ticket supplement {${ticketIndex}} for order {${orderId}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }

  async downloadInvoice({ orderId, customerId, isGuestOrder, kind }: {
    orderId: string,
    customerId: string,
    isGuestOrder: boolean,
    kind: InvoiceKind,
  }): Promise<OperationResponse<string>> {
    const endpoint = this.endpointProvider.downloadInvoice;

    try {
      if(this.authenticationService.isSigningIn) {
        await firstValueFrom(this.authenticationService.isSigningIn$.pipe(
          filter((x) => x === false),
          first()
        ));
      }

      const context = await createHttpsAuthContext(this.authenticationService.authState?.user);
      const callable = this.cloudFunctionsProvider.getHttpsCallable(
        endpoint,
        context,
      );

      const response = await callable.call({
        parameters: {
          'orderId': orderId,
          'customerId': customerId,
          'isGuestOrder': isGuestOrder,
          'kind': kind,
        }
      });

      const success = MapUtils.getBooleanOrNull(response.data, 'success') ?? false;
      if(success) {
        const data = MapUtils.getString(response.data, 'result');
        return OperationResponse.success(data);
      }
      else {
        const errorType = parseInTimeApiError(MapUtils.getStringOrNull(response.data, 'error') ?? ErrorType.Unknown);
        logWarning(`Failed to download invoice of kind {${kind}} for order {${orderId}} because of a cloud error: ${errorType}`);
        return OperationResponse.error(errorType);
      }
    }
    catch(error) {
      logError(`Failed to call ${endpoint} function: ${(error instanceof CloudFunctionsException) ? error.message : error}`);
      return OperationResponse.error(ErrorType.FailedToCallCloudFunction);
    }
  }
}
